import { CaseReadPublic, PaginatedCaseReadPublic } from "@/store/clientApi";
import { ThumbnailDataObject } from "@/typed/CaseStateData";
import { ModelLabel } from "@/typed/ModelTypes";
import { getCaseThumbnailsFromIndexedDB, Store, updateThumbnailDataInIndexedDB } from "@/utils/indexeddb";
import { createAsyncThunk, createSlice, PayloadAction } from "@reduxjs/toolkit";

interface ThumbnailsCacheState {
  [id: string]: { value: boolean; last: number };
}

interface ThumbnailUrl {
  url: string;
  label: ModelLabel;
  type: "model" | "scan";
}

const caseToThumbnailUrls = (dentalCase: CaseReadPublic) => {
  const {
    upper_model_thumbnail_presigned_url,
    upper_scan_thumbnail_presigned_url,
    lower_model_thumbnail_presigned_url,
    lower_scan_thumbnail_presigned_url,
    combined_model_thumbnail_presigned_url,
    combined_scan_thumbnail_presigned_url
  } = dentalCase;

  const availableThumbnails: ThumbnailUrl[] = [];
  if (upper_scan_thumbnail_presigned_url !== undefined) {
    availableThumbnails.push({
      url: upper_scan_thumbnail_presigned_url,
      label: "upper",
      type: "scan"
    });
  }
  if (lower_scan_thumbnail_presigned_url !== undefined) {
    availableThumbnails.push({
      url: lower_scan_thumbnail_presigned_url,
      label: "lower",
      type: "scan"
    });
  }
  if (combined_scan_thumbnail_presigned_url !== undefined) {
    availableThumbnails.push({
      url: combined_scan_thumbnail_presigned_url,
      label: "combined",
      type: "scan"
    });
  }

  if (upper_model_thumbnail_presigned_url !== undefined) {
    availableThumbnails.push({
      url: upper_model_thumbnail_presigned_url,
      label: "upper",
      type: "model"
    });
  }
  if (lower_model_thumbnail_presigned_url !== undefined) {
    availableThumbnails.push({
      url: lower_model_thumbnail_presigned_url,
      label: "lower",
      type: "model"
    });
  }
  if (combined_model_thumbnail_presigned_url !== undefined) {
    availableThumbnails.push({
      url: combined_model_thumbnail_presigned_url,
      label: "combined",
      type: "model"
    });
  }

  return availableThumbnails;
};

const initialState: ThumbnailsCacheState = {};

const cacheCaseThumbnails = createAsyncThunk(
  "thumbnailsCache/cacheCaseThumbnails",
  async (casesData: PaginatedCaseReadPublic | undefined, { dispatch, getState }) => {
    if (!casesData?.content) {
      return;
    }
    // Cache thumbnails
    await Promise.allSettled(
      casesData.content.map(async (dentalCase) => {
        const caseId = dentalCase.id.toString();

        // Check if the thumbnail is already being cached
        const isCaching = (getState() as { thumbnailsCache: ThumbnailsCacheState }).thumbnailsCache[caseId]?.value;
        if (isCaching) {
          return;
        }

        const { id, status } = dentalCase;
        const availableThumbnails = caseToThumbnailUrls(dentalCase);

        if (availableThumbnails.length === 0) {
          return;
        }

        // Set case as being cached
        dispatch(setThumbnailCaching({ id: caseId, value: true }));

        const scanThumbnails = ((await getCaseThumbnailsFromIndexedDB(id, status, Store.ScanThumbnailStore, true)) ?? []).map(
          (thumbnail) => thumbnail.label
        );
        const modelThumbnails = ((await getCaseThumbnailsFromIndexedDB(id, status, Store.OutputThumbnailStore, true)) ?? []).map(
          (thumbnail) => thumbnail.label
        );

        // Check for missing thumbnails
        const missingThumbnails: ThumbnailUrl[] = availableThumbnails.filter((thumbnail) =>
          thumbnail.type === "scan" ? !scanThumbnails.includes(thumbnail.label) : !modelThumbnails.includes(thumbnail.label)
        );

        if (missingThumbnails.length === 0) {
          dispatch(setThumbnailCaching({ id: caseId, value: false }));
          return;
        }

        // Download missing thumbnails
        const newScanThumbnails: ThumbnailDataObject[] = [];
        const newModelThumbnails: ThumbnailDataObject[] = [];
        for (const { url, type, label } of missingThumbnails) {
          try {
            const response = await fetch(url);
            if (!response.ok) {
              continue;
            }

            const thumbnailBlob = await response.blob();
            const data: ThumbnailDataObject = {
              data: await thumbnailBlob.arrayBuffer(),
              type: "png",
              label: label
            };

            if (type === "model") {
              newModelThumbnails.push(data);
            } else {
              newScanThumbnails.push(data);
            }
          } catch (e) {//eslint-disable-line
            continue;
          }
        }

        // Update cache
        if (newModelThumbnails.length > 0) {
          try {
            await updateThumbnailDataInIndexedDB(id.toString(), status, newModelThumbnails, Store.OutputThumbnailStore);
          } catch (e) {} //eslint-disable-line
        }
        if (newScanThumbnails.length > 0) {
          try {
            await updateThumbnailDataInIndexedDB(id.toString(), status, newScanThumbnails, Store.ScanThumbnailStore);
          } catch (e) {} //eslint-disable-line
        }

        dispatch(setThumbnailCaching({ id: caseId, value: false }));
        if (newScanThumbnails.length > 0 || newModelThumbnails.length > 0) {
          dispatch(setLastFetched({ id: id.toString() }));
        }
      })
    );
  }
);

const thumbnailsCacheSlice = createSlice({
  name: "thumbnailsCache",
  initialState,
  reducers: {
    setThumbnailCaching(state, action: PayloadAction<{ id: string; value: boolean }>) {
      const { id, value } = action.payload;
      state[id] = { value, last: state[id]?.last ?? Date.now() };
    },
    setLastFetched(state, action: PayloadAction<{ id: string }>) {
      const { id } = action.payload;
      state[id] = { ...state[id], last: Date.now() };
    }
  },
  selectors: {
    selectIsThumbnailCaching(state, id: string) {
      return state[id]?.value === true;
    },
    selectLastFetched(state, id: string) {
      return state[id]?.last;
    }
  }
});

const { setThumbnailCaching, setLastFetched } = thumbnailsCacheSlice.actions;
export const { selectIsThumbnailCaching, selectLastFetched } = thumbnailsCacheSlice.selectors;
export { cacheCaseThumbnails, setLastFetched };
export default thumbnailsCacheSlice.reducer;
